import { Platform } from "react-native";
import store from "../store";
import { logout, loginStart } from "../store/slices/authSlice";
import acquireTokenInteractive from "../components/auth/acquireTokenInteractive";

import { reportError } from "./Instrumentation";

/**
 * Trigger MSAL interactive login flow
 */
const triggerMsalLogin = async () => {
  try {
    if (Platform.OS === "web") {
      // For web, use MSAL browser library
      const { PublicClientApplication } = require("@azure/msal-browser");
      const msalInstance = new PublicClientApplication({
        auth: {
          clientId:
            process.env.MSAL_CLIENT_ID ||
            "e3a34560-5d71-4055-b2d7-6ab366704411",
          authority:
            process.env.MSAL_AUTHORITY ||
            "https://login.microsoftonline.com/42cc3295-cd0e-449c-b98e-5ce5b560c1d3",
          redirectUri:
            process.env.MSAL_REDIRECT_URI_WEB || window.location.origin,
        },
        cache: {
          cacheLocation: "localStorage",
          storeAuthStateInCookie: true,
        },
      });

      await msalInstance.initialize();

      // Trigger interactive login
      await msalInstance.loginRedirect({
        scopes: ["User.Read"],
        prompt: "select_account", // Force account selection
      });
    } else {
      // For native platforms, redirect to root and let the app handle auth
      console.log("Triggering authentication flow for native platform");
    }
  } catch (error) {
    console.error("Error triggering MSAL login:", error);
    reportError({
      severity: "ERROR",
      message: error instanceof Error ? error.message : String(error),
      stackTrace: error instanceof Error ? error.stack : undefined,
    });
    // Fallback to page redirect
    if (Platform.OS === "web") {
      window.location.href = "/";
    }
  }
};

/**
 * Handles redirection to login page when authentication fails
 * This is primarily used for session timeouts or token expirations
 */
export const redirectToLogin = async () => {
  console.log("Authentication required - initiating login flow");

  // Dispatch logout action to clear auth state
  store.dispatch(logout());

  // Start login process
  store.dispatch(loginStart());

  // Handle platform-specific redirects
  if (Platform.OS === "web") {
    // Try to trigger MSAL interactive login first
    triggerMsalLogin().catch(() => {
      // Fallback to simple redirect if MSAL fails
      console.log(
        "MSAL interactive login failed, falling back to page redirect"
      );
      window.location.href = "/";
    });
  } else {
    // For native platforms, try interactive authentication first
    try {
      console.log("Attempting interactive authentication for native platform");
      const token = await acquireTokenInteractive();
      
      if (token) {
        console.log("Interactive authentication successful, token acquired");
        // Update the token in the Redux store
        store.dispatch({ 
          type: "auth/tokenAcquisitionSuccess", 
          payload: token 
        });
        return; // Success, no need to redirect
      }
    } catch (error) {
      console.error("Interactive authentication failed:", error);
    }
    
    // If interactive auth fails, the app should still handle the auth state change
    // through the MsalProvider which will show the login screen
    console.log("Session expired - auth state cleared, MsalProvider will handle login");
  }
};

/**
 * Determines if an error is related to authentication failure
 */
export const isAuthError = (error: any): boolean => {
  // Check for token expiration or auth-related errors
  if (error?.response?.status === 401) {
    return true;
  }

  // Check for specific error message patterns
  const errorMessage = error?.message || "";
  const responseData = error?.response?.data || {};
  const responseMessage =
    typeof responseData === "string"
      ? responseData
      : JSON.stringify(responseData);

  return (
    errorMessage.includes("idToken") ||
    errorMessage.includes("Cannot read properties of null") ||
    errorMessage.includes("Can't read properties of null") ||
    errorMessage.includes("null (reading") ||
    responseMessage.includes("Authentication failed") ||
    responseMessage.includes("token expired") ||
    responseMessage.includes("authentication required")
  );
};
